import { useState } from 'react'
import { Modal } from './Modal'
import { Mono } from './primitives/Fact'
import type { MigrationLicenseType } from '../api/types'
import { Notice } from './primitives/Notice'

// Pre-adjustment confirmation (ATC-130 / ATC-122).
//
// A NEW SIBLING of ConfirmRotateModal, copying its shape. `ConfirmRotateModal`
// is deliberately NOT refactored, generalised, or touched: `FD-ADR-038` §1.11
// promoted that exact dialog into shared suite Tier 1 FROM ATC, naming it as
// the standard the other two products must match. Extracting a shared base
// would change the thing the suite just standardised on, to save a few lines.
//
// WHY AN ADJUSTMENT EARNS THE FULL AMBER→RED ESCALATION. It reduces a
// customer's PAID entitlement, and it is not undoable: the ledger is
// append-only, so the only reversal is another grant, which lands as a separate
// row and reads as a new purchase rather than a correction. It can also refuse
// (`adjustment_below_committed`) precisely because it could otherwise
// retroactively invalidate migrations that are already running.
export function ConfirmAdjustmentModal({
  accountId,
  accountName,
  licenseType,
  amount,
  reason,
  currentTotal,
  committed,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  accountId: string
  accountName: string
  licenseType: MigrationLicenseType
  /** Negative. The magnitude is what the operator typed. */
  amount: number
  reason: string
  currentTotal: number
  /** Pool: in-use slots. Consumable: burned + open reservations. */
  committed: number
  busy: boolean
  error: string | null
  onConfirm: () => void
  onCancel: () => void
}) {
  const [ack, setAck] = useState(false)

  const bucket = licenseType === 'pool' ? 'pool capacity' : 'consumable credits'
  const after = currentTotal + amount

  return (
    <Modal title="Reduce this customer's balance?" onClose={onCancel} dismissal="deliberate">
      {/* ATC-136: static impact copy, deliberately left as a div — see the
          matching note in ConfirmRotateModal. */}
      <div className="notice notice-error">
        This <strong>permanently reduces a paid entitlement</strong>. The ledger is append-only,
        so the only reversal is a new grant — which reads as a purchase, not a correction.
      </div>

      <p>
        Adjusting <strong>{accountName || 'this customer'}</strong>{' '}
        (<code className="cei">{accountId}</code>):
      </p>
      <ul className="checklist">
        <li>
          {bucket} <Mono>{currentTotal}</Mono> → <Mono>{after}</Mono>
          <span className="muted"> (adjustment of {amount})</span>
        </li>
        <li>
          <Mono>{committed}</Mono> already committed to running migrations
          <span className="muted">
            {' '}
            — the server refuses any adjustment that would go below this
          </span>
        </li>
        <li>
          reason recorded in the audit trail verbatim
          <span className="muted"> (it reaches Application Insights)</span>
        </li>
      </ul>
      <p className="muted">
        The correction is stored as a new ledger row, so the trail shows both the original
        grant and this fix. Reason: “{reason}”
      </p>

      {error && <Notice tone="error">{error}</Notice>}

      <label className="ack-row">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
        <span>I understand this reduces what the customer has paid for.</span>
      </label>

      <div className="modal-actions">
        <button onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button className="btn-danger" disabled={!ack || busy} onClick={onConfirm}>
          {busy ? 'Adjusting…' : 'Apply adjustment'}
        </button>
      </div>
    </Modal>
  )
}
